// Opt-in location sharing. While the app is open and the toggle is on, the phone
// watches its position and posts it (throttled) for the /map page. A web app
// cannot report in the background, so the map shows "last seen" positions.
import { api } from "./api";

const KEY = "shareLocation";
const MIN_INTERVAL_MS = 15_000;
const MIN_MOVE_M = 25;

export const isSharing = () => localStorage.getItem(KEY) === "1";

export function rememberedDriverId(): number | null {
  try {
    const id = JSON.parse(localStorage.getItem("driver") ?? "null")?.id;
    return Number.isInteger(id) ? id : null;
  } catch {
    return null;
  }
}

let watchId: number | null = null;
let last: { t: number; lat: number; lng: number } | null = null;

function distanceM(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const r = 6_371_000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(x));
}

function stopWatching() {
  if (watchId !== null) navigator.geolocation.clearWatch(watchId);
  watchId = null;
  last = null;
}

/** Starts the watcher; resolves after the first fix, rejects if permission is denied. */
function startWatching(driverId: number): Promise<void> {
  stopWatching();
  return new Promise((resolve, reject) => {
    let first = true;
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const now = Date.now();
        const moved = !last || distanceM(last, here) >= MIN_MOVE_M;
        const stale = !last || now - last.t >= MIN_INTERVAL_MS;
        if (first || (moved && stale) || (last && now - last.t >= 4 * MIN_INTERVAL_MS)) {
          last = { t: now, ...here };
          api.sendLocation(driverId, { ...here, accuracy: pos.coords.accuracy ?? null }).catch(() => {});
        }
        if (first) {
          first = false;
          resolve();
        }
      },
      (err) => {
        if (first) {
          first = false;
          stopWatching();
          reject(new Error(err.code === err.PERMISSION_DENIED ? "Location access was denied" : "Could not get location"));
        }
      },
      { enableHighAccuracy: false, maximumAge: 10_000, timeout: 20_000 },
    );
  });
}

export async function setSharing(on: boolean): Promise<void> {
  const driverId = rememberedDriverId();
  if (on) {
    if (!driverId) throw new Error("Pick your name in the box tracker first");
    if (!("geolocation" in navigator)) throw new Error("This browser has no location support");
    await startWatching(driverId);
    localStorage.setItem(KEY, "1");
  } else {
    stopWatching();
    localStorage.removeItem(KEY);
    if (driverId) api.clearLocation(driverId).catch(() => {});
  }
  window.dispatchEvent(new Event("location-sharing"));
}

/** Call once on app start: resumes sharing if it was left on. */
export function resumeSharing() {
  const driverId = rememberedDriverId();
  if (isSharing() && driverId && "geolocation" in navigator) startWatching(driverId).catch(() => {});
}
